import { Form, FormState } from 'informed';
import styles from '../../../components/TyAdvancedSearch/Components/Layout.module.css';
import { PageTitle } from '../../../components/texts';
import { CloseIcon } from '../../../components/icons';
import Modal from 'react-modal';
import { useState } from 'react';
import { CompanyDefaultSettingsData } from '@tymbe/schema/company-default-settings.interface';
import { SecondaryButton } from '../../../components/buttons';
import { useMutation, useQueryClient } from 'react-query';
import feathersClient from '../../../apiClient';
import CompanyShiftDefaultSettingsForm from './CompanyShiftDefaultSettingsForm';
import { ContestDuration } from '@tymbe/schema/enums';
import { CompanyData } from '@tymbe/schema/company.interface';
import moment from 'moment';
import { ErrorAlert, SuccessAlert } from '../../../components/alerts';
import toHHMM from '../../../utils/toHHMM';

type Props = {
  companyId: number;
  initialData: CompanyDefaultSettingsData;
};

export type CompanyDefaultSettingsFormData = {
  shift_contest_end_variant?: ContestDuration | null;
  shift_contest_end_time?: string | null;
};

const CompanyShiftDefaultSettings = ({ initialData, companyId }: Props) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const qClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    ['PatchCompanyDefaultSettings', initialData.id],
    async (data: Partial<CompanyData>) => {
      return feathersClient.service('company').patch(companyId, data);
    }
  );

  const onSubmit = async ({ values }: FormState<CompanyDefaultSettingsFormData>) => {
    const company: Partial<CompanyData> = {
      defaultSettings: {
        ...initialData,
        max_credits: initialData?.max_credits ? Number(initialData.max_credits) : undefined,
        max_payment_base: initialData?.max_payment_base ? Number(initialData.max_payment_base) : undefined,
        min_payment_base: initialData?.min_payment_base ? Number(initialData.min_payment_base) : undefined,
        min_credits: initialData?.min_credits ? Number(initialData.min_credits) : undefined,
        shift_contest_end_variant: values.shift_contest_end_variant,
        shift_contest_end_time: values.shift_contest_end_time,
        shift_contest_end_time_zone: moment.tz.guess(),
      } as CompanyDefaultSettingsData,
    };

    try {
      await mutateAsync(company);
      qClient.invalidateQueries(['FetchCompany', String(companyId)]);
      SuccessAlert('Výchozí nastavení směn bylo uloženo');
      setShowModal(false);
    } catch (err) {
      console.error(err);
      ErrorAlert('Nastavení směn se nepodařilo uložit');
    }
  };

  return (
    <>
      <SecondaryButton
        className="ty-button-secondary"
        buttontext="Výchozí nastavení směn"
        onClick={() => setShowModal(true)}
      />
      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        className={styles.modal}
        overlayClassName={styles.overlay}
        ariaHideApp={false}
      >
        <div className={styles.header}>
          <div>
            <PageTitle>Výchozí nastavení směn</PageTitle>
          </div>
          <div>
            <button
              aria-label="close button"
              type="button"
              className={styles.close}
              onClick={() => setShowModal(false)}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
        <Form
          initialValues={{
            shift_contest_end_variant: initialData?.shift_contest_end_variant,
            shift_contest_end_time: toHHMM(initialData?.shift_contest_end_time),
          }}
          onSubmit={async (values: FormState<CompanyDefaultSettingsFormData>) => {
            await onSubmit(values);
          }}
        >
          <CompanyShiftDefaultSettingsForm isLoading={isLoading} setShowModal={setShowModal} initialData={initialData} />
        </Form>
      </Modal>
    </>
  );
};

export default CompanyShiftDefaultSettings;
